import { Table, SortTH } from 'foundations-components/transitional/components/Table';
import { useState } from 'react';
import { useTheme } from 'foundations-theming/utils';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import UserAvatarLink from '../common/UserAvatarLink';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedPercentage from 'I18n/components/FormattedPercentage';
import { useContentAssignments } from '../../queries/contentQueries';
import ACTableSkeleton from 'academy-common-lib/components/loading/ACTableSkeleton';
import { NavMarker } from 'react-rhumb';
import ApiError from 'academy-common-lib/components/error/ApiError';
import MyTeamTablePagination from '../common/TablePagination';
import { useTableSorting } from '../../hooks/tableHooks';
import { getOffsetPosition, getPaginatedPosition } from '../../utils/paginationUtils';
import EmptyTable from '../common/EmptyTable';
import { useParams } from 'quartz-routing/react-router';
import { getContentStatus } from '../../utils/contentUtils';
import ContentTypeLabel from '../assignedContent/ContentTypeLabel';
import { useUserInfo } from '../../../accounts/accountsQueries.legacy';
import { dueDateOptions, completedDateOptions } from '../../constants/filters';
import { getStartOfDay, getEndOfDay } from '../../utils/dateTimeUtils';
import { useContentTableStore } from '../../store/myTeamTableStateStore';
import DateTableCell from '../common/DateTableCell';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { Button } from 'foundations-components/transitional/components/Button';
import { ContentAssignPanel } from 'academy-common-lib/features/contentAssignments/components/ContentAssignPanel';
import PortalIdParser from 'PortalIdParser';
import { useDataFetchingClient } from 'academy-common-lib/utils/DataFetchingClient';
import { GET_ALL_ASSIGNED_USER_PROGRESS_QUERY_FIELD_NAME } from '../../../../../__generated__/chirp/com/hubspot/learning/center/credentials/rpc/AssignedContentChirpService.dfc.new';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const buildDateRange = (selected, options) => {
  if (!selected) return undefined;
  const {
    startDate,
    endDate
  } = options[selected].getValue();
  return {
    startDate: getStartOfDay(startDate),
    endDate: getEndOfDay(endDate)
  };
};
const ContentTableForStatus = ({
  status,
  tableName
}) => {
  var _total, _ref, _contentAssignments$r;
  const [selectedContent, setSelectedContent] = useState(null);
  const portalId = PortalIdParser.get();
  const client = useDataFetchingClient();
  const {
    userInfo
  } = useUserInfo();
  const isAllTab = status === undefined;
  const theme = useTheme();
  const {
    isDesktop
  } = useMediaQuery();
  const store = useContentTableStore(tableName);
  const {
    state: {
      page,
      pageSize,
      sortBy,
      sortOrder,
      search,
      teams,
      users,
      assignedByMe,
      contentIds,
      contentTypes,
      dueDate,
      completedDate
    },
    actions: {
      setPage,
      setPageSize,
      setSortBy,
      setSortOrder
    }
  } = store;
  const {
    handleSortChange,
    getSortDirectionForColumn
  } = useTableSorting({
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder
  });
  const offsetPosition = getOffsetPosition(page, pageSize);
  const {
    contentAssignments,
    contentAssignmentsLoading,
    contentAssignmentsError,
    previousData
  } = useContentAssignments({
    pageLength: pageSize,
    offset: offsetPosition,
    sortOrder,
    sortField: sortBy,
    contentNameQuery: search,
    teams,
    users,
    contentIds,
    contentTypes: contentTypes,
    status,
    assignedBy: assignedByMe ? userInfo === null || userInfo === void 0 ? void 0 : userInfo.user.id : undefined,
    dueDate: buildDateRange(dueDate, dueDateOptions),
    completedAt: buildDateRange(completedDate, completedDateOptions)
  });
  const totalResults = (_total = (_ref = contentAssignments !== null && contentAssignments !== void 0 ? contentAssignments : previousData) === null || _ref === void 0 ? void 0 : _ref.total) !== null && _total !== void 0 ? _total : 0;
  if (contentAssignmentsError) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "MY_TEAM_CONTENT_TABLE_API_ERROR",
      children: /*#__PURE__*/_jsx(ApiError, {
        error: {
          status: 500
        }
      })
    });
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [contentAssignmentsLoading ? /*#__PURE__*/_jsx(ACTableSkeleton, {
      columns: isAllTab ? 5 : 4,
      condensed: true,
      rows: pageSize
    }) : contentAssignments !== null && contentAssignments !== void 0 && (_contentAssignments$r = contentAssignments.results) !== null && _contentAssignments$r !== void 0 && _contentAssignments$r.length ? /*#__PURE__*/_jsx(NavMarker, {
      name: "MY_TEAM_CONTENT_TABLE_LOADED",
      children: /*#__PURE__*/_jsxs(Table, {
        condensed: true,
        "data-test-id": "content-table",
        hover: true,
        responsive: true,
        children: [/*#__PURE__*/_jsxs("colgroup", {
          children: [/*#__PURE__*/_jsx("col", {
            style: {
              width: '35%',
              borderInlineEnd: `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '20%'
            }
          }), status !== 'COMPLETED' && /*#__PURE__*/_jsx("col", {
            style: {
              width: '15%'
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '15%'
            }
          }), status !== 'INCOMPLETE' && /*#__PURE__*/_jsx("col", {
            style: {
              width: '15%'
            }
          })]
        }), /*#__PURE__*/_jsx("thead", {
          children: /*#__PURE__*/_jsxs("tr", {
            children: [/*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('CONTENT_NAME'),
              sort: getSortDirectionForColumn('CONTENT_NAME'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.content.tableColumns.contentName"
              })
            }), /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('TEAM_MEMBER_NAME'),
              sort: getSortDirectionForColumn('TEAM_MEMBER_NAME'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.content.tableColumns.name"
              })
            }), status !== 'COMPLETED' && /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('PROGRESS'),
              sort: getSortDirectionForColumn('PROGRESS'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.content.tableColumns.progress"
              })
            }), /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('DUE_DATE'),
              sort: getSortDirectionForColumn('DUE_DATE'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.content.tableColumns.dueDate"
              })
            }), status !== 'INCOMPLETE' && /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('COMPLETED_DATE'),
              sort: getSortDirectionForColumn('COMPLETED_DATE'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.content.tableColumns.completed"
              })
            })]
          })
        }), /*#__PURE__*/_jsx("tbody", {
          children: contentAssignments.results.map((assignment, index) => {
            const position = getPaginatedPosition(offsetPosition, index);
            return /*#__PURE__*/_jsxs("tr", {
              "data-test-id": "content-table-data-row",
              children: [/*#__PURE__*/_jsx("td", {
                "data-test-id": "content-name-cell",
                children: /*#__PURE__*/_jsx(ContentTypeLabel, {
                  contentId: assignment.contentId,
                  contentName: assignment.contentName,
                  contentType: assignment.contentType
                })
              }), /*#__PURE__*/_jsx("td", {
                "data-test-id": "user-cell",
                children: /*#__PURE__*/_jsx(UserAvatarLink, {
                  displayAvatar: isDesktop,
                  position: position,
                  userEmail: assignment.userEmail,
                  userId: assignment.userId,
                  userName: assignment.userName
                })
              }), status !== 'COMPLETED' && /*#__PURE__*/_jsx("td", {
                "data-test-id": "progress-cell",
                children: /*#__PURE__*/_jsx(FormattedPercentage, {
                  value: assignment.progressPercentage
                })
              }), /*#__PURE__*/_jsx("td", {
                "data-test-id": "due-date-cell",
                children: /*#__PURE__*/_jsx(Button, {
                  onClick: () => {
                    setSelectedContent({
                      contentId: assignment.contentId,
                      assignmentId: assignment.assignmentId,
                      contentName: assignment.contentName,
                      contentType: assignment.contentType
                    });
                  },
                  use: "transparent",
                  children: assignment.dueDate ? /*#__PURE__*/_jsx(FormattedDateTime, {
                    format: "ll",
                    type: "utc",
                    value: assignment.dueDate
                  }) : /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.myTeam.content.addDueDate"
                  })
                })
              }), status !== 'INCOMPLETE' && /*#__PURE__*/_jsx(DateTableCell, {
                testId: "completed-date-cell",
                value: assignment.completedDate
              })]
            }, `${assignment.contentId}-${assignment.userId}`);
          })
        })]
      })
    }) : /*#__PURE__*/_jsx(EmptyTable, {
      navMarkerName: "MY_TEAM_CONTENT_TABLE_EMPTY"
    }), /*#__PURE__*/_jsx(MyTeamTablePagination, {
      onPageChange: setPage,
      onPageSizeChange: setPageSize,
      page: page,
      pageSize: pageSize,
      totalResults: totalResults
    }), selectedContent && portalId && /*#__PURE__*/_jsx(ContentAssignPanel, {
      assignmentIds: [selectedContent.assignmentId],
      contentId: selectedContent.contentId,
      contentName: selectedContent.contentName,
      contentType: selectedContent.contentType,
      onClose: () => setSelectedContent(null),
      onSuccess: () => {
        void client.refetchQueries({
          updateCache(cache) {
            cache.evict({
              fieldName: GET_ALL_ASSIGNED_USER_PROGRESS_QUERY_FIELD_NAME
            });
          }
        });
        setSelectedContent(null);
      },
      portalId: portalId
    })]
  });
};
const ContentTable = ({
  tableName
}) => {
  const {
    contentStatus
  } = useParams();
  const status = getContentStatus(contentStatus);
  return /*#__PURE__*/_jsx(ContentTableForStatus, {
    status: status,
    tableName: tableName
  }, status);
};
export default ContentTable;